import React from 'react';

/**
 * Outline icon set for planning screens.
 *
 * All icons are 14x14 SVG with stroke-only style (no fill),
 * 1.5px stroke width, rounded line caps. Designed to look like
 * Tabler Icons / Heroicons outline style.
 *
 * Usage:
 *   <OutlineIcon name="plus" />
 *   <OutlineIcon name="calendar" size={16} />
 *   <OutlineIcon name="bolt" color="#805ad5" />
 */

export type OutlineIconName =
    | 'plus' | 'minus' | 'chevron-left' | 'chevron-right'
    | 'arrow-left' | 'arrow-right' | 'check' | 'x'
    | 'calendar' | 'clock' | 'user' | 'users'
    | 'bolt' | 'refresh' | 'sync' | 'pencil'
    | 'trash' | 'copy' | 'link' | 'external'
    | 'tag' | 'flag' | 'star' | 'eye'
    | 'expand'
    | 'menu' | 'dots' | 'search' | 'filter'
    | 'circle' | 'circle-dot' | 'warning' | 'info'
    | 'circle-check' | 'circle-x' | 'circle-arrow-right'
    | 'plus-circle' | 'minus-circle' | 'user-plus' | 'calendar-days'
    | 'chart-bars';

interface OutlineIconProps {
    name: OutlineIconName;
    size?: number;
    color?: string;
    strokeWidth?: number;
    style?: React.CSSProperties;
    title?: string;
}

export function OutlineIcon(props: OutlineIconProps) {
    const { name, size = 14, color = 'currentColor', strokeWidth = 1.5, style, title } = props;
    const common = {
        width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
        stroke: color, strokeWidth, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
        style: { display: 'inline-block', verticalAlign: 'middle', ...style },
    };

    switch (name) {
        case 'plus':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
        case 'minus':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="5" y1="12" x2="19" y2="12" /></svg>;
        case 'chevron-left':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="15 6 9 12 15 18" /></svg>;
        case 'chevron-right':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="9 6 15 12 9 18" /></svg>;
        case 'arrow-left':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>;
        case 'arrow-right':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>;
        case 'check':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="20 6 9 17 4 12" /></svg>;
        case 'x':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
        case 'calendar':
            return <svg {...common} aria-hidden="true"><title>{title}</title><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
        case 'clock':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>;
        case 'user':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>;
        case 'users':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c0-3.3 3-6 6.5-6s6.5 2.7 6.5 6" /><circle cx="17" cy="9" r="2.5" /><path d="M14 14c2.8 0 5 1.8 5 4" /></svg>;
        case 'bolt':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polygon points="13 2 4 14 11 14 10 22 20 10 13 10 13 2" /></svg>;
        case 'refresh':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" /></svg>;
        case 'sync':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="21 12 21 4 13 4" /><polyline points="3 12 3 20 11 20" /><path d="M21 4l-4 4a9 9 0 0 1-13 3" /><path d="M3 20l4-4a9 9 0 0 0 13-3" /></svg>;
        case 'pencil':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
        case 'trash':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>;
        case 'copy':
            return <svg {...common} aria-hidden="true"><title>{title}</title><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>;
        case 'link':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>;
        case 'external':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>;
        case 'tag':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>;
        case 'flag':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>;
        case 'star':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>;
        case 'eye':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
        case 'expand':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>;
        case 'menu':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>;
        case 'dots':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></svg>;
        case 'search':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
        case 'filter':
            return <svg {...common} aria-hidden="true"><title>{title}</title><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>;
        case 'circle':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /></svg>;
        case 'circle-dot':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" fill={color} stroke="none" /></svg>;
        case 'warning':
            return <svg {...common} aria-hidden="true"><title>{title}</title><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
        case 'info':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>;
        case 'circle-check':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><polyline points="8 12 11 15 16 9" /></svg>;
        case 'circle-x':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><line x1="9" y1="9" x2="15" y2="15" /><line x1="15" y1="9" x2="9" y2="15" /></svg>;
        case 'circle-arrow-right':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><polyline points="8 12 12 16 16 12" /><line x1="12" y1="8" x2="12" y2="16" /></svg>;
        case 'plus-circle':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></svg>;
        case 'minus-circle':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="12" cy="12" r="9" /><line x1="8" y1="12" x2="16" y2="12" /></svg>;
        case 'user-plus':
            return <svg {...common} aria-hidden="true"><title>{title}</title><circle cx="10" cy="7" r="3.5" /><path d="M3 20c0-3.5 3.2-6.5 7-6.5s7 3 7 6.5" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="16" y1="11" x2="22" y2="11" /></svg>;
        case 'calendar-days':
            return <svg {...common} aria-hidden="true"><title>{title}</title><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><circle cx="8" cy="14" r="0.5" fill={color} stroke="none" /><circle cx="12" cy="14" r="0.5" fill={color} stroke="none" /><circle cx="16" cy="14" r="0.5" fill={color} stroke="none" /></svg>;
        case 'chart-bars':
            return <svg {...common} aria-hidden="true"><title>{title}</title><line x1="4" y1="20" x2="20" y2="20" /><line x1="7" y1="16" x2="7" y2="20" /><line x1="12" y1="9" x2="12" y2="20" /><line x1="17" y1="13" x2="17" y2="20" /></svg>;
        default:
            return null;
    }
}
